// TEMPORARY: the browser side of the sidebar-to-conversation-tab session handoff (see the
// ChatwootBridge `handoff` option). A CRM that posts the user's `auth` to sidebar dashboard apps
// only leaves the conversation tab nothing to sign in with; the sidebar stores a handoff token here
// and the conversation tab exchanges it for a session. Remove with the `handoff` option once the
// CRM posts `auth` to conversation apps too.
//
// The handoff token is a bearer credential (limited by the server to one account and user, at most
// 24 hours). It is the only thing this SDK ever writes to storage; the session token and the CRM
// credentials stay in memory. Every storage access is wrapped: storage may be blocked or full.

/** The slice of `Storage` used here; injectable for tests. */
export interface HandoffStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface StoredHandoff {
  token: string;
  accountId: number;
  userId: number;
  /** Unix seconds, as the server reported them. */
  issuedAt: number;
  expiresAt: number;
}

export const DEFAULT_HANDOFF_KEY_PREFIX = 'waforms:handoff';

const positiveInt = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

/** `window.localStorage`, or null where reading it throws (blocked storage, sandboxed frames). */
export function browserStorage(): HandoffStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export class HandoffStore {
  readonly #prefix: string;
  readonly #storage: () => HandoffStorage | null;
  readonly #now: () => number;

  constructor(options: {
    prefix?: string;
    storage?: (() => HandoffStorage | null) | undefined;
    /** Unix seconds; for tests. */
    now?: () => number;
  }) {
    this.#prefix = options.prefix ?? DEFAULT_HANDOFF_KEY_PREFIX;
    this.#storage = options.storage ?? browserStorage;
    this.#now = options.now ?? (() => Date.now() / 1000);
  }

  key(accountId: number, userId: number): string {
    return `${this.#prefix}:${accountId}:${userId}`;
  }

  /** The stored handoff of this account and user, if it is well formed. The server checks the rest. */
  read(accountId: number, userId: number): StoredHandoff | null {
    let raw: string | null;
    try {
      raw = this.#storage()?.getItem(this.key(accountId, userId)) ?? null;
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      const value = JSON.parse(raw) as Partial<StoredHandoff>;
      if (
        typeof value.token === 'string' &&
        value.token !== '' &&
        value.accountId === accountId &&
        value.userId === userId &&
        typeof value.issuedAt === 'number' &&
        typeof value.expiresAt === 'number'
      ) {
        return {
          token: value.token,
          accountId,
          userId,
          issuedAt: value.issuedAt,
          expiresAt: value.expiresAt,
        };
      }
    } catch {
      // not ours or corrupted: dropped below
    }
    this.remove(accountId, userId);
    return null;
  }

  /** True when there is no stored handoff or less than half of its lifetime is left. */
  needsRefresh(accountId: number, userId: number): boolean {
    const stored = this.read(accountId, userId);
    if (!stored) return true;
    const lifetime = stored.expiresAt - stored.issuedAt;
    return stored.expiresAt - this.#now() < lifetime / 2;
  }

  write(handoff: StoredHandoff): boolean {
    if (!positiveInt(handoff.accountId) || !positiveInt(handoff.userId)) return false;
    try {
      const storage = this.#storage();
      if (!storage) return false;
      storage.setItem(this.key(handoff.accountId, handoff.userId), JSON.stringify(handoff));
      return true;
    } catch {
      return false;
    }
  }

  remove(accountId: number, userId: number): void {
    try {
      this.#storage()?.removeItem(this.key(accountId, userId));
    } catch {
      // nothing to do: storage is blocked
    }
  }
}
